export const COURSE_LEVELS = ['Beginner', 'Intermediate', 'Advanced'];
export const COURSE_STATUSES = ['Active', 'Inactive'];
export const LESSON_TYPES = ['Video', 'Article', 'PDF', 'Quiz'];

/** Match backend ImageStorageHelper DefaultMaxBytes (5 MB). */
export const THUMBNAIL_MAX_BYTES = 5 * 1024 * 1024;
export const THUMBNAIL_ACCEPT = 'image/jpeg,image/jpg,image/png,image/webp,.jpg,.jpeg,.png,.webp';
export const THUMBNAIL_ALLOWED_MIME = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/pjpeg'];
export const THUMBNAIL_ALLOWED_EXT = ['.jpg', '.jpeg', '.png', '.webp'];

/** Match backend EducationVideoMaxBytesMb default (100 MB). */
export const VIDEO_MAX_BYTES = 100 * 1024 * 1024;
export const VIDEO_ACCEPT = 'video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov';
export const VIDEO_ALLOWED_MIME = [
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'video/mpeg',
  'application/mp4',
];
export const VIDEO_ALLOWED_EXT = ['.mp4', '.webm', '.mov'];

/** Match backend EducationPdfMaxBytesMb default (15 MB). */
export const PDF_MAX_BYTES = 15 * 1024 * 1024;
export const PDF_ACCEPT = 'application/pdf,.pdf';
export const PDF_ALLOWED_MIME = ['application/pdf', 'application/x-pdf'];
export const PDF_ALLOWED_EXT = ['.pdf'];

export const formatBytes = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const pick = (obj, ...keys) => {
  for (const key of keys) {
    if (obj?.[key] != null && obj[key] !== '') return obj[key];
  }
  return undefined;
};

export const formatCourseDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime()) || d.getFullYear() < 1970) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const emptyCourseForm = () => ({
  title: '',
  shortDescription: '',
  description: '',
  category: '',
  level: 'Beginner',
  duration: '',
  language: '',
  status: 'Active',
});

export const validateThumbnailFile = (file) => {
  if (!file) return '';
  const name = String(file.name || '').toLowerCase();
  const ext = name.includes('.') ? `.${name.split('.').pop()}` : '';
  const mime = String(file.type || '').toLowerCase();
  const mimeOk =
    !mime ||
    mime === 'application/octet-stream' ||
    THUMBNAIL_ALLOWED_MIME.includes(mime);
  const extOk = THUMBNAIL_ALLOWED_EXT.includes(ext);
  if (!extOk || !mimeOk) {
    return 'Thumbnail must be a JPG, PNG, or WEBP image';
  }
  if (file.size > THUMBNAIL_MAX_BYTES) {
    return 'Thumbnail must be 5 MB or smaller';
  }
  return '';
};
export const normalizeCourse = (row) => {
  if (!row || typeof row !== 'object') return null;
  const id = Number(pick(row, 'courseId', 'CourseId', 'id'));
  if (!Number.isFinite(id) || id <= 0) return null;

  const statusRaw = pick(row, 'status', 'Status');
  const isActiveRaw = pick(row, 'isActive', 'IsActive');
  let isActive = true;
  if (typeof isActiveRaw === 'boolean') isActive = isActiveRaw;
  else if (typeof statusRaw === 'string') {
    isActive = statusRaw.toLowerCase() !== 'inactive';
  }

  const modulesRaw = pick(row, 'modules', 'Modules') || [];
  const modules = Array.isArray(modulesRaw)
    ? modulesRaw.map(normalizeModule).filter(Boolean)
    : [];

  return {
    id,
    affiliateUserId: Number(pick(row, 'affiliateUserId', 'AffiliateUserId')) || null,
    title: pick(row, 'title', 'Title') || '',
    shortDescription: pick(row, 'shortDescription', 'ShortDescription') || '',
    description: pick(row, 'description', 'Description') || '',
    thumbnailUrl: pick(row, 'thumbnailUrl', 'ThumbnailUrl') || '',
    category: pick(row, 'category', 'Category') || '',
    level: pick(row, 'level', 'Level') || '',
    duration: pick(row, 'duration', 'Duration') || '',
    language: pick(row, 'language', 'Language') || '',
    status: isActive ? 'Active' : 'Inactive',
    isActive,
    createdDateTime: pick(row, 'createdDateTime', 'CreatedDateTime') || null,
    updatedDateTime: pick(row, 'updatedDateTime', 'UpdatedDateTime') || null,
    modules,
  };
};

export const normalizeModule = (row) => {
  if (!row || typeof row !== 'object') return null;
  const id = Number(pick(row, 'courseModuleId', 'CourseModuleId', 'id'));
  if (!Number.isFinite(id) || id <= 0) return null;

  const isActiveRaw = pick(row, 'isActive', 'IsActive');
  const statusRaw = pick(row, 'status', 'Status');
  let isActive = true;
  if (typeof isActiveRaw === 'boolean') isActive = isActiveRaw;
  else if (typeof statusRaw === 'string') isActive = statusRaw.toLowerCase() !== 'inactive';

  const displayOrder = Number(
    pick(row, 'displayOrder', 'DisplayOrder', 'sortOrder', 'SortOrder')
  );

  return {
    id,
    courseId: Number(pick(row, 'courseId', 'CourseId')) || null,
    courseTitle: pick(row, 'courseTitle', 'CourseTitle') || '',
    title: pick(row, 'title', 'Title') || '',
    description: pick(row, 'description', 'Description') || '',
    displayOrder: Number.isFinite(displayOrder) && displayOrder > 0 ? displayOrder : 0,
    isActive,
    status: isActive ? 'Active' : 'Inactive',
    lessonCount: Number(pick(row, 'lessonCount', 'LessonCount')) || 0,
    createdDateTime: pick(row, 'createdDateTime', 'CreatedDateTime') || null,
    updatedDateTime: pick(row, 'updatedDateTime', 'UpdatedDateTime') || null,
  };
};

export const emptyModuleForm = (nextOrder = 1) => ({
  title: '',
  description: '',
  displayOrder: String(nextOrder || 1),
  status: 'Active',
});

export const validateModuleForm = (form) => {
  if (!form?.title?.trim()) return 'Module title is required';
  if (form.title.trim().length > 255) return 'Module title must be 255 characters or fewer';
  const order = Number(form.displayOrder);
  if (!Number.isFinite(order) || order < 1 || !Number.isInteger(order)) {
    return 'Display order must be a positive whole number';
  }
  return '';
};

export const normalizeLesson = (row) => {
  if (!row || typeof row !== 'object') return null;
  const id = Number(pick(row, 'courseLessonId', 'CourseLessonId', 'id'));
  if (!Number.isFinite(id) || id <= 0) return null;

  const isActiveRaw = pick(row, 'isActive', 'IsActive');
  const statusRaw = pick(row, 'status', 'Status');
  let isActive = true;
  if (typeof isActiveRaw === 'boolean') isActive = isActiveRaw;
  else if (typeof statusRaw === 'string') isActive = statusRaw.toLowerCase() !== 'inactive';

  const displayOrder = Number(
    pick(row, 'displayOrder', 'DisplayOrder', 'sortOrder', 'SortOrder')
  );
  const lessonType =
    pick(row, 'lessonType', 'LessonType', 'contentType', 'ContentType') || 'Article';

  return {
    id,
    moduleId: Number(pick(row, 'courseModuleId', 'CourseModuleId', 'moduleId')) || null,
    courseId: Number(pick(row, 'courseId', 'CourseId')) || null,
    title: pick(row, 'title', 'Title') || '',
    description: pick(row, 'description', 'Description') || '',
    lessonType,
    displayOrder: Number.isFinite(displayOrder) && displayOrder > 0 ? displayOrder : 0,
    isActive,
    status: isActive ? 'Active' : 'Inactive',
    hasContent: Boolean(pick(row, 'hasContent', 'HasContent')),
    createdDateTime: pick(row, 'createdDateTime', 'CreatedDateTime') || null,
    updatedDateTime: pick(row, 'updatedDateTime', 'UpdatedDateTime') || null,
  };
};

export const normalizeLessonContent = (row) => {
  if (!row || typeof row !== 'object') return null;
  const id = Number(pick(row, 'courseLessonId', 'CourseLessonId', 'id'));
  if (!Number.isFinite(id) || id <= 0) return null;

  const isActiveRaw = pick(row, 'isActive', 'IsActive');
  const statusRaw = pick(row, 'status', 'Status');
  let isActive = true;
  if (typeof isActiveRaw === 'boolean') isActive = isActiveRaw;
  else if (typeof statusRaw === 'string') isActive = statusRaw.toLowerCase() !== 'inactive';

  const lessonType =
    pick(row, 'lessonType', 'LessonType', 'contentType', 'ContentType') || 'Article';

  return {
    id,
    moduleId: Number(pick(row, 'courseModuleId', 'CourseModuleId', 'moduleId')) || null,
    courseId: Number(pick(row, 'courseId', 'CourseId')) || null,
    courseTitle: pick(row, 'courseTitle', 'CourseTitle') || '',
    moduleTitle: pick(row, 'moduleTitle', 'ModuleTitle') || '',
    title: pick(row, 'title', 'Title') || '',
    lessonType,
    isActive,
    status: isActive ? 'Active' : 'Inactive',
    hasContent: Boolean(pick(row, 'hasContent', 'HasContent')),
    articleContent: pick(row, 'articleContent', 'ArticleContent') || '',
    contentFileUrl: pick(row, 'contentFileUrl', 'ContentFileUrl') || '',
    contentOriginalFileName:
      pick(row, 'contentOriginalFileName', 'ContentOriginalFileName') || '',
  };
};

export const validateVideoFile = (file) => {
  if (!file) return '';
  const name = String(file.name || '').toLowerCase();
  const ext = name.includes('.') ? `.${name.split('.').pop()}` : '';
  const mime = String(file.type || '').toLowerCase();
  const mimeOk =
    !mime || mime === 'application/octet-stream' || VIDEO_ALLOWED_MIME.includes(mime);
  const extOk = VIDEO_ALLOWED_EXT.includes(ext);
  if (!extOk || !mimeOk) {
    return 'Video must be MP4, WEBM, or MOV';
  }
  if (file.size > VIDEO_MAX_BYTES) {
    return 'Video must be 100 MB or smaller';
  }
  return '';
};

export const validatePdfFile = (file) => {
  if (!file) return '';
  const name = String(file.name || '').toLowerCase();
  const ext = name.includes('.') ? `.${name.split('.').pop()}` : '';
  const mime = String(file.type || '').toLowerCase();
  const mimeOk =
    !mime || mime === 'application/octet-stream' || PDF_ALLOWED_MIME.includes(mime);
  const extOk = PDF_ALLOWED_EXT.includes(ext);
  if (!extOk || !mimeOk) {
    return 'File must be a PDF';
  }
  if (file.size > PDF_MAX_BYTES) {
    return 'PDF must be 15 MB or smaller';
  }
  return '';
};

export const emptyLessonForm = (nextOrder = 1) => ({
  title: '',
  description: '',
  lessonType: 'Article',
  displayOrder: String(nextOrder || 1),
  status: 'Active',
});

export const validateLessonForm = (form) => {
  if (!form?.title?.trim()) return 'Lesson title is required';
  if (form.title.trim().length > 255) return 'Lesson title must be 255 characters or fewer';
  if (!LESSON_TYPES.includes(form?.lessonType)) {
    return 'Lesson type must be Video, Article, PDF, or Quiz';
  }
  const order = Number(form.displayOrder);
  if (!Number.isFinite(order) || order < 1 || !Number.isInteger(order)) {
    return 'Display order must be a positive whole number';
  }
  return '';
};

export const validateCourseForm = (form) => {
  if (!form?.title?.trim()) return 'Course title is required';
  if (!form?.category?.trim()) return 'Category is required';
  if (!COURSE_LEVELS.includes(form?.level)) {
    return 'Level must be Beginner, Intermediate, or Advanced';
  }
  return '';
};

export const normalizeRichText = (html) => {
  if (!html || typeof html !== 'string') return null;
  const trimmed = html.trim();
  if (!trimmed) return null;
  const textOnly = trimmed
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
  if (!textOnly) return null;
  return trimmed;
};

export const isRichTextHtml = (value) =>
  typeof value === 'string' && /<\/?[a-z][\s\S]*>/i.test(value);

export const toCoursePayload = (form) => ({
  title: form.title.trim(),
  shortDescription: form.shortDescription?.trim() || '',
  description: normalizeRichText(form.description) || '',
  category: form.category.trim(),
  level: form.level,
  duration: form.duration?.trim() || '',
  language: form.language?.trim() || '',
  status: form.status === 'Inactive' ? 'Inactive' : 'Active',
});

export const emptyQuizQuestionForm = () => ({
  questionText: '',
  explanation: '',
  marks: '1',
  status: 'Active',
  options: [
    { text: '', isCorrect: true },
    { text: '', isCorrect: false },
  ],
});

export const normalizeQuizSettings = (row) => {
  if (!row || typeof row !== 'object') return null;
  const id = Number(pick(row, 'courseLessonId', 'CourseLessonId', 'lessonId'));
  if (!Number.isFinite(id) || id <= 0) return null;

  const isActiveRaw = pick(row, 'isActive', 'IsActive');
  const statusRaw = pick(row, 'status', 'Status');
  let isActive = true;
  if (typeof isActiveRaw === 'boolean') isActive = isActiveRaw;
  else if (typeof statusRaw === 'string') isActive = statusRaw.toLowerCase() !== 'inactive';

  const maxAttemptsRaw = pick(row, 'maxAttempts', 'MaxAttempts');
  const timeRaw = pick(row, 'timeLimitMinutes', 'TimeLimitMinutes');

  return {
    id,
    moduleId: Number(pick(row, 'courseModuleId', 'CourseModuleId')) || null,
    courseId: Number(pick(row, 'courseId', 'CourseId')) || null,
    courseTitle: pick(row, 'courseTitle', 'CourseTitle') || '',
    moduleTitle: pick(row, 'moduleTitle', 'ModuleTitle') || '',
    title: pick(row, 'title', 'Title') || '',
    isActive,
    status: isActive ? 'Active' : 'Inactive',
    passingScore: Number(pick(row, 'passingScore', 'PassingScore')) || 70,
    maxAttempts:
      maxAttemptsRaw == null || maxAttemptsRaw === ''
        ? null
        : Number(maxAttemptsRaw),
    timeLimitMinutes:
      timeRaw == null || timeRaw === '' ? null : Number(timeRaw),
    totalQuestions: Number(pick(row, 'totalQuestions', 'TotalQuestions')) || 0,
    totalMarks: Number(pick(row, 'totalMarks', 'TotalMarks')) || 0,
  };
};

export const normalizeQuizQuestion = (row) => {
  if (!row || typeof row !== 'object') return null;
  const id = Number(pick(row, 'quizQuestionId', 'QuizQuestionId', 'id'));
  if (!Number.isFinite(id) || id <= 0) return null;

  const isActiveRaw = pick(row, 'isActive', 'IsActive');
  const statusRaw = pick(row, 'status', 'Status');
  let isActive = true;
  if (typeof isActiveRaw === 'boolean') isActive = isActiveRaw;
  else if (typeof statusRaw === 'string') isActive = statusRaw.toLowerCase() !== 'inactive';

  const displayOrder = Number(pick(row, 'displayOrder', 'DisplayOrder', 'sortOrder', 'SortOrder'));
  const optionsRaw = pick(row, 'options', 'Options') || [];
  const options = Array.isArray(optionsRaw)
    ? optionsRaw
        .map((o) => {
          if (!o || typeof o !== 'object') return null;
          return {
            id: Number(pick(o, 'quizOptionId', 'QuizOptionId', 'id')) || null,
            optionText: pick(o, 'optionText', 'OptionText', 'text', 'Text') || '',
            isCorrect: Boolean(pick(o, 'isCorrect', 'IsCorrect')),
            displayOrder: Number(pick(o, 'displayOrder', 'DisplayOrder', 'sortOrder')) || 0,
          };
        })
        .filter(Boolean)
    : [];

  return {
    id,
    lessonId: Number(pick(row, 'courseLessonId', 'CourseLessonId', 'lessonId')) || null,
    questionText: pick(row, 'questionText', 'QuestionText') || '',
    explanation: pick(row, 'explanation', 'Explanation') || '',
    marks: Number(pick(row, 'marks', 'Marks')) || 1,
    displayOrder: Number.isFinite(displayOrder) && displayOrder > 0 ? displayOrder : 0,
    isActive,
    status: isActive ? 'Active' : 'Inactive',
    optionCount: Number(pick(row, 'optionCount', 'OptionCount')) || options.length,
    options,
    createdDateTime: pick(row, 'createdDateTime', 'CreatedDateTime') || null,
    updatedDateTime: pick(row, 'updatedDateTime', 'UpdatedDateTime') || null,
  };
};

export const validateQuizSettingsForm = (form) => {
  const score = Number(form?.passingScore);
  if (!Number.isFinite(score) || score < 1 || score > 100 || !Number.isInteger(score)) {
    return 'Passing score must be a whole number from 1 to 100';
  }
  if (form?.maxAttempts !== '' && form?.maxAttempts != null) {
    const attempts = Number(form.maxAttempts);
    if (!Number.isFinite(attempts) || attempts < 1 || !Number.isInteger(attempts)) {
      return 'Maximum attempts must be a positive whole number (or blank for unlimited)';
    }
  }
  if (form?.timeLimitMinutes !== '' && form?.timeLimitMinutes != null) {
    const mins = Number(form.timeLimitMinutes);
    if (!Number.isFinite(mins) || mins < 1 || !Number.isInteger(mins)) {
      return 'Time limit must be a positive whole number of minutes (or blank for unlimited)';
    }
  }
  return '';
};

export const validateQuizQuestionForm = (form) => {
  if (!form?.questionText?.trim()) return 'Question text is required';
  if (form.questionText.trim().length > 2000) {
    return 'Question text must be 2000 characters or fewer';
  }
  const marks = Number(form?.marks);
  if (!Number.isFinite(marks) || marks < 1 || !Number.isInteger(marks)) {
    return 'Marks must be a positive whole number';
  }
  const options = Array.isArray(form?.options) ? form.options : [];
  if (options.length < 2) return 'At least 2 options are required';
  if (options.length > 6) return 'A question may have at most 6 options';
  const texts = new Set();
  let correct = 0;
  for (const opt of options) {
    const text = String(opt?.text || '').trim();
    if (!text) return 'Every option requires text';
    const key = text.toLowerCase();
    if (texts.has(key)) return 'Duplicate option text is not allowed';
    texts.add(key);
    if (opt.isCorrect) correct += 1;
  }
  if (correct !== 1) return 'Exactly one option must be marked as the correct answer';
  return '';
};

